import React, { useMemo } from 'react'
import CanvasLogo from '../../../assets/icons/canvas-logo.svg'
import { LEVEL, LEVEL_ORDER, REQUIREMENT } from './utils/constants/levels'
import { STATUS } from './utils/constants/statuses'
import { isCompleteStatus, hasAdvancedPastLevel } from './utils/displayUtils'
import { ARTICLE_PATHS } from './utils/constants/articleUrls'
import { CANVAS_URLS } from './utils/constants/externalUrls'
import { LevelTrackerSection, RequirementsSection } from './ProgressTrackerContent'
import { useMembershipJourneyContext } from './MembershipJourneyContext'
import {
  Container,
  Header,
  TitleSection,
  Title,
  AssociateHeader,
  ProgramInfoItem,
  ProgramInfoLabel,
  ProgramInfoLink,
  PortalButton,
  PortalButtonText
} from './Styles/ProgressTracker.styles'

const ProgressTracker = ({
  activeLevel = LEVEL.QUALIFYING,
  onLevelClick
}) => {
  const { me, pgmProgress } = useMembershipJourneyContext()
  
  const {
    levels = [],
    currentLevelKey,
    mostRecentCompletedLevelKey,
    careerPath,
    patStatus,
    suspensions = []
  } = pgmProgress || {}

  const trackerLevels = useMemo(() => {
    return levels.map(level => {
      return {
        key: level.key,
        name: level.name,
        completed: level.status === STATUS.COMPLETE,
        active: level.key === activeLevel,
        locked: level.status === STATUS.LOCKED,
        progress: hasAdvancedPastLevel(level.key, currentLevelKey, mostRecentCompletedLevelKey) ? 100 : level.progressPercent,
        status: level.status,
        hasIncompleteRequirements: level.key === LEVEL.QUALIFYING && patStatus && patStatus !== STATUS.COMPLETE
      }
    })
  }, [levels, activeLevel, patStatus, currentLevelKey, mostRecentCompletedLevelKey])

  const currentLevel = useMemo(() => 
    levels.find(l => l.key === activeLevel),
    [levels, activeLevel]
  )

  // Currently only showing requirements that an Associate would see (ie., that enabled them to progress past Qualifying and view this dashboard)
  // Filters out inaccurate/confusing statuses that are not yet applicable (e.g., failed/registered PAT)
  // Expired background check is hidden once the user has advanced past Qualifying (historical tab view)
  const qualifyingDisplayRequirements = useMemo(() => {
    if (activeLevel !== LEVEL.QUALIFYING || !currentLevel) return null

    const qualifyingRequirementOrder = {
      [REQUIREMENT.BACKGROUND_CHECK]: 0,
      [REQUIREMENT.COURSE_MANUALS]: 1,
      [REQUIREMENT.PLAYING_ABILITY_TEST]: 2,
      [REQUIREMENT.APPLICATION]: 3,
      [REQUIREMENT.PURCHASE]: 4,
    }

    const rawBackgroundCheckStatus = me?.prospect?.backgroundCheck?.status
    const hasPendingBackgroundCheckOrder = me?.prospect?.backgroundCheckOrder?.awaitingModeration
    const shouldOverrideBackgroundCheckToPending =
      !rawBackgroundCheckStatus || rawBackgroundCheckStatus === 'EXPIRED'
    const effectiveRequirements = (currentLevel.requirements || []).map(req => {
      if (
        req.key === REQUIREMENT.BACKGROUND_CHECK &&
        !isCompleteStatus(req.status) &&
        shouldOverrideBackgroundCheckToPending &&
        hasPendingBackgroundCheckOrder
      ) {
        return {
          ...req,
          status: STATUS.PENDING
        }
      }

      return req
    })

    const pastQualifying = hasAdvancedPastLevel(
      LEVEL.QUALIFYING,
      currentLevelKey,
      mostRecentCompletedLevelKey
    )

    return effectiveRequirements
      .filter(req => {
        if (req.key === REQUIREMENT.BACKGROUND_CHECK) {
          if (req.status === STATUS.EXPIRED && pastQualifying) return false
          return true
        }
        if (isCompleteStatus(req.status)) return true
        if (req.key === REQUIREMENT.PLAYING_ABILITY_TEST && req.status === STATUS.QUALIFIED) return true
        if (req.key === REQUIREMENT.PURCHASE && (req.status === STATUS.OPEN || req.status === STATUS.LOCKED)) return true
        return false
      })
      .sort((a, b) => {
        const aOrder = qualifyingRequirementOrder[a.key] ?? Number.MAX_SAFE_INTEGER
        const bOrder = qualifyingRequirementOrder[b.key] ?? Number.MAX_SAFE_INTEGER
        return aOrder - bOrder
      })
  }, [
    activeLevel,
    currentLevel,
    currentLevelKey,
    mostRecentCompletedLevelKey,
    me?.prospect?.backgroundCheck?.status,
    me?.prospect?.backgroundCheckOrder?.awaitingModeration,
  ])

  // Use filtered subset of requirements for the qualifying level; raw requirements for Levels 1 - 3
  const displayLevel = useMemo(() => {
    if (!currentLevel) return null
    if (activeLevel !== LEVEL.QUALIFYING || qualifyingDisplayRequirements === null) return currentLevel
    return { ...currentLevel, requirements: qualifyingDisplayRequirements }
  }, [currentLevel, activeLevel, qualifyingDisplayRequirements])

  // Show a simplified level complete banner as a fallback when we can't display individual requirement statuses (ie., missing data from previous/completed levels)
  const shouldShowLevelCompleteBanner = useMemo(() => {
    if (!displayLevel) return false
    
    // Qualifying level: fall back to the simplified banner when missing qualifying requirements data
    if (displayLevel.key === LEVEL.QUALIFYING) {
      const requirements = displayLevel.requirements || []
      return requirements.length === 0
    }
    
    const requirements = displayLevel.requirements || []
    const allRequirementsComplete = requirements.length > 0 && requirements.every(req => 
      isCompleteStatus(req.status)
    )
    
    if (hasAdvancedPastLevel(activeLevel, currentLevelKey, mostRecentCompletedLevelKey)) {
      if (requirements.length === 0 || !allRequirementsComplete) {
        return true
      }
    }
    
    return false
  }, [displayLevel, activeLevel, currentLevelKey, mostRecentCompletedLevelKey])

  const levelCompletionDate = currentLevel?.completionDate || null

  const patRequirementWithWarning = useMemo(() => {
    if (activeLevel !== LEVEL.QUALIFYING || !patStatus || patStatus === STATUS.COMPLETE) return null

    return {
      key: REQUIREMENT.PLAYING_ABILITY_TEST,
      name: 'Playing Ability Test (PAT)',
      status: patStatus
    }
  }, [activeLevel, patStatus])

  const shouldShowPATWarning = patStatus && patStatus !== STATUS.COMPLETE

  // Build additional requirements list (incl. PAT for non-qualifying levels)
  const userCurrentLevelIndex = LEVEL_ORDER.indexOf(currentLevelKey)
  const activeLevelIndex = LEVEL_ORDER.indexOf(activeLevel)
  const shouldShowAdditionalRequirements = activeLevel !== LEVEL.QUALIFYING && activeLevelIndex >= userCurrentLevelIndex && !hasAdvancedPastLevel(activeLevel, currentLevelKey, mostRecentCompletedLevelKey)

  const additionalRequirements = useMemo(() => {
    if (activeLevel === LEVEL.QUALIFYING || !patStatus || patStatus === STATUS.COMPLETE || !shouldShowAdditionalRequirements) {
      return []
    }
    
    return [{
      key: REQUIREMENT.PLAYING_ABILITY_TEST,
      name: 'Playing Ability Test (PAT)',
      status: patStatus
    }]
  }, [activeLevel, patStatus, shouldShowAdditionalRequirements])

  return (
    <Container>
      <Header>
        <TitleSection>
          <Title>My Path to Membership</Title>
          {careerPath && (
            <ProgramInfoLink href={`https://www.pga.org${ARTICLE_PATHS.CAREER_PATHS}`} target="_blank" rel="noopener noreferrer">
              {careerPath} ›
            </ProgramInfoLink>
          )}
        </TitleSection>
        {currentLevelKey && currentLevelKey !== LEVEL.QUALIFYING && (
          <AssociateHeader>
            <ProgramInfoItem>
              <ProgramInfoLabel>Associate Program</ProgramInfoLabel>
              <ProgramInfoLink href="https://www.pga.org/membership/associate-program/" target="_blank" rel="noopener noreferrer">View Guide ›</ProgramInfoLink>
            </ProgramInfoItem>
          </AssociateHeader>
        )}
      </Header>

      <LevelTrackerSection 
        levels={trackerLevels}
        onLevelClick={onLevelClick}
        suspensions={suspensions}
        currentLevelKey={currentLevelKey}
        activeLevel={activeLevel}
      />

      <RequirementsSection
        shouldShowLevelCompleteBanner={shouldShowLevelCompleteBanner}
        currentLevel={displayLevel}
        levelCompletionDate={levelCompletionDate}
        patRequirementWithWarning={patRequirementWithWarning}
        additionalRequirements={additionalRequirements}
        activeLevel={activeLevel}
        shouldShowPATWarning={shouldShowPATWarning}
        shouldShowAdditionalRequirements={shouldShowAdditionalRequirements}
        onLevelClick={onLevelClick}
      />

      <PortalButton href={CANVAS_URLS.HOME} target="_blank" rel="noopener noreferrer">
        <img src={CanvasLogo} alt="Canvas Logo" />
        <PortalButtonText>Education Portal</PortalButtonText>
      </PortalButton>
    </Container>
  )
}

export default ProgressTracker
